// Runtime configuration, read once from /config.json before the app renders (WEB-05), so the same bundle runs on
// the dev stack, staging and production. The deployment writes the file; public/config.json holds the dev-stack
// values. Nothing in it is secret.
export type AppConfig = {
  /** Origin of vision-api, without /v1. Empty means the app's own origin (the dev server proxies /v1). */
  apiBase: string;
  /** Keycloak realms URL, for example https://auth.iot.creavision.tech/realms. */
  oidcBase: string;
  /** The public OIDC client of the web app in both realms (VisionPlatform deploy/keycloak). */
  clientId: string;
  /**
   * Which deployment this is. Staging-only console tools (Test mode, WEB-DBG-CMP-06) are hidden on production. Absent
   * or unknown reads as production, so a deployment that forgets the key never shows them.
   */
  environment: AppEnvironment;
};

export const ENVIRONMENTS = ["production", "staging", "dev"] as const;
export type AppEnvironment = (typeof ENVIRONMENTS)[number];

let config: AppConfig | undefined;

export function parseConfig(raw: unknown): AppConfig {
  if (typeof raw !== "object" || raw === null) throw new Error("config.json is not an object");
  const r = raw as Record<string, unknown>;
  const str = (k: string, fallback?: string): string => {
    const v = r[k] ?? fallback;
    if (typeof v !== "string") throw new Error(`config.json: ${k} must be a string`);
    return v;
  };
  return {
    apiBase: str("apiBase", "").replace(/\/+$/, ""),
    oidcBase: str("oidcBase").replace(/\/+$/, ""),
    clientId: str("clientId", "vision-web"),
    environment: ENVIRONMENTS.find((e) => e === r.environment) ?? "production",
  };
}

export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<AppConfig> {
  const res = await fetchImpl("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`config.json: HTTP ${res.status}`);
  config = parseConfig(await res.json());
  return config;
}

export function getConfig(): AppConfig {
  if (!config) throw new Error("config not loaded");
  return config;
}

/** The configuration when it is loaded; widgets drawn outside the app (the fixture tests) have none. */
export function maybeConfig(): AppConfig | undefined {
  return config;
}

export function setConfigForTests(c: AppConfig): void {
  config = c;
}
